import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Select } from 'src/design/atoms/Select';
import { TextInput } from 'src/design/atoms/TextInput';
import { IconSearch } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import {
  KnowledgeArticleListItemDto,
  useKnowledgeArticles,
} from 'src/api/queries/knowledgeArticles/useKnowledgeArticles';
import { useOffices } from 'src/api/queries/organization/useOffices';
import useDebounce from 'src/hooks/useDebounce';
import { CreateButton } from 'src/design/atoms/CreateButton';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { KnowledgeEditor } from 'src/routes/Settings/AIAssistant/KnowledgeEditor';
import { formatDateTime } from 'src/utils';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

const STATUS_COLOR: Record<string, string> = {
  draft: 'gray',
  published: 'green',
  archived: 'yellow',
};

const getColumns = (): MRT_ColumnDef<KnowledgeArticleListItemDto>[] => [
  { accessorKey: 'title', header: 'Title' },
  {
    id: 'offices',
    header: 'Offices',
    accessorFn: (row) => row.offices,
    Cell: ({ row }) => (
      <Group gap={4}>
        {row.original.offices.length === 0 ? (
          <Badge color="blue" variant="light">
            Global
          </Badge>
        ) : (
          row.original.offices.map((office) => (
            <Badge key={office.id} color="gray" variant="light">
              {office.name}
            </Badge>
          ))
        )}
      </Group>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    Cell: ({ cell }) => {
      const status = cell.getValue<string>();
      return (
        <Badge color={STATUS_COLOR[status] ?? 'gray'} variant="light">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated',
    Cell: ({ row }) => {
      const author = row.original.updatedByName;
      const formatted = formatDateTime(row.original.updatedAt);
      return author ? `${formatted} by ${author}` : formatted;
    },
  },
];

export const KnowledgeTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [officeId, setOfficeId] = useState<string | null>('');
  const [status, setStatus] = useState<string | null>('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: offices } = useOffices();
  const officeOptions = useMemo(
    () => [
      { value: '', label: 'All offices' },
      ...(offices ?? []).map((office) => ({
        value: String(office.id),
        label: office.name,
      })),
    ],
    [offices],
  );

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'ai-assistant-knowledge-articles',
    columns,
    defaultSorting: [{ id: 'updatedAt', desc: true }],
  });
  const { pagination, setPagination } = tableState;

  const filter = {
    search: debouncedSearch || undefined,
    officeId: officeId ? Number(officeId) : undefined,
    status: status || undefined,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useKnowledgeArticles(filter);

  // Reset to page 1 on filter change so we never request a page past the filtered result set.
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [debouncedSearch, officeId, status]);

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: false,
      enableClickToCopy: false,
      columns,
      data: data?.items ?? [],
      manualPagination: true,
      manualSorting: false,
      manualFiltering: true,
      // Hide the column filter row — only the toolbar search + selects are wired to the server.
      enableColumnFilters: false,
      mantineTableBodyRowProps: ({ row }) => ({
        onClick: () => setSelectedId(row.original.id),
        style: { cursor: 'pointer' },
      }),
      renderTopToolbarCustomActions: () => (
        <Group>
          <TextInput
            placeholder="Search articles..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            w={260}
          />
          <Select
            data={officeOptions}
            value={officeId}
            onChange={setOfficeId}
            w={200}
            aria-label="Filter by office"
          />
          <Select
            data={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            w={180}
            aria-label="Filter by status"
          />
          <CreateButton onClick={() => setCreating(true)}>
            New Article
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
    onRefresh: refetch,
  });

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        Knowledge articles teach the AI Assistant about your company — policies,
        procedures, and terminology. The assistant sees every published
        article&apos;s title and summary, and reads the full body when it&apos;s
        relevant to a question. Drafts and archived articles are never shown to
        the assistant.
      </Text>

      {table}
      <KnowledgeEditor
        articleId={selectedId}
        opened={selectedId != null || creating}
        onClose={() => {
          setSelectedId(null);
          setCreating(false);
        }}
      />
    </Stack>
  );
};
